import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { articles, getArticle } from '@/mocks/articles';
import { getChannel } from '@/mocks/channels';
import { formatCount } from '@/components/feature/ArticleCard';
import { ArticleCard } from '@/components/feature/ArticleCard';
import { ChannelPill } from '@/components/feature/ChannelPill';
import SidePanel from '@/pages/article/components/SidePanel';

export default function ArticlePage() {
  const { id } = useParams();
  const article = getArticle(Number(id));
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  const channel = article ? getChannel(article.channel) : undefined;

  useSeo({
    title: article ? `${article.title} - 热瓜速递` : '内容不存在 - 热瓜速递',
    description: article
      ? `${article.summary} 本文来自${channel?.name ?? ''}栏目，由${article.author}撰写，热瓜速递整理发布。`
      : '你访问的文章不存在或已被删除。',
    keywords: article ? `${channel?.name ?? ''},${article.tags.join(',')},吃瓜` : '',
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            image: article.cover,
            datePublished: article.date,
            author: { '@type': 'Person', name: article.author },
            articleSection: channel?.name,
            inLanguage: 'zh-CN',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: channel?.name ?? '', item: `/channel/${article.channel}` },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <i className="ri-file-search-line text-5xl text-foreground-300" aria-hidden="true" />
        <h1 className="mt-4 text-xl font-bold text-foreground-900">这篇“瓜”被抢走了</h1>
        <p className="mt-2 text-sm text-foreground-500">文章可能已下架或链接有误，去首页看看其他内容吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-700"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = articles
    .filter((a) => a.id !== article.id)
    .sort((a, b) => {
      const sameA = a.channel === article.channel ? 0 : 1;
      const sameB = b.channel === article.channel ? 0 : 1;
      return sameA - sameB || b.views - a.views;
    })
    .slice(0, 3);

  const likeCount = article.views + (liked ? 1 : 0);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      {/* breadcrumb */}
      <nav className="flex items-center gap-1.5 py-4 text-xs text-foreground-400" aria-label="面包屑">
        <Link to="/" className="inline-flex items-center gap-1 transition-colors hover:text-primary-600">
          <i className="ri-home-5-line" aria-hidden="true" /> 首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
        <Link to={`/channel/${article.channel}`} className="transition-colors hover:text-primary-600">
          {channel?.name}
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
        <span className="truncate text-foreground-600">{article.title}</span>
      </nav>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* 正文 */}
        <article className="min-w-0 lg:col-span-2">
          <header>
            <div className="flex items-center gap-3">
              <ChannelPill slug={article.channel} />
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">
                <i className="ri-eye-line" aria-hidden="true" /> {formatCount(article.views)} 阅读
              </span>
            </div>
            <h1 className="mt-3 text-2xl font-black leading-snug text-foreground-950 md:text-[2rem]">
              {article.title}
            </h1>
            <p className="mt-3 text-base leading-relaxed text-foreground-500">{article.summary}</p>

            <div className="mt-4 flex flex-wrap items-center gap-3 border-y border-background-200/70 py-3 text-sm text-foreground-500">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-base font-bold text-primary-700">
                {article.author.slice(0, 1)}
              </span>
              <div className="leading-tight">
                <p className="font-semibold text-foreground-800">{article.author}</p>
                <p className="text-xs text-foreground-400">热瓜速递 · 编辑</p>
              </div>
              <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-time-line" aria-hidden="true" /> {article.date} · {article.read}分钟
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  aria-label="点赞"
                  onClick={() => setLiked(!liked)}
                  className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors ${
                    liked
                      ? 'border-primary-300 bg-primary-100 text-primary-700'
                      : 'border-background-300/70 text-foreground-500 hover:border-primary-200 hover:text-primary-600'
                  }`}
                >
                  <i className={liked ? 'ri-heart-fill' : 'ri-heart-line'} aria-hidden="true" />
                  {formatCount(likeCount)}
                </button>
                <button
                  type="button"
                  onClick={copyLink}
                  className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors ${
                    copied
                      ? 'border-accent-300 bg-accent-100 text-accent-800'
                      : 'border-background-300/70 text-foreground-500 hover:border-primary-200 hover:text-primary-600'
                  }`}
                >
                  <i className={copied ? 'ri-check-line' : 'ri-link'} aria-hidden="true" />
                  {copied ? '链接已复制' : '复制链接'}
                </button>
              </div>
            </div>
          </header>

          {/* 封面 */}
          <figure className="mt-6">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title}-热瓜速递`}
              className="aspect-[16/9] w-full rounded-lg object-cover object-top"
            />
            <figcaption className="mt-2 text-center text-xs text-foreground-400">
              {article.title} · 配图示意
            </figcaption>
          </figure>

          {/* 正文 */}
          <div className="mt-7 space-y-5 text-[15px] leading-7 text-foreground-700 md:text-base md:leading-8">
            {article.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {/* tags */}
          <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200/70 pt-5">
            <span className="text-sm font-semibold text-foreground-500">话题标签：</span>
            {article.tags.map((t) => (
              <Link
                key={t}
                to={`/channel/${article.channel}`}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-price-tag-3-line" aria-hidden="true" /> {t}
              </Link>
            ))}
          </div>

          {/* 作者卡 */}
          <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-50 p-5 sm:flex-row sm:items-center">
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xl font-black text-background-50">
              {article.author.slice(0, 1)}
            </span>
            <div className="flex-1">
              <p className="text-base font-bold text-foreground-900">{article.author}</p>
              <p className="mt-1 text-sm leading-relaxed text-foreground-500">
                热瓜速递专栏作者，专注{channel?.name}一线资讯，坚持理性吃瓜、真实优先，用轻松的方式聊透热点。
              </p>
            </div>
            <Link
              to={`/channel/${article.channel}`}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-200 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
            >
              查看TA的栏目
            </Link>
          </div>
        </article>

        {/* 侧栏 */}
        <div className="min-w-0">
          <SidePanel current={article.id} />
        </div>
      </div>

      {/* 相关推荐 */}
      <section className="mt-12" aria-label="相关推荐">
        <div className="mb-4 flex items-center justify-between border-b border-background-200/70 pb-3">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
            <i className="ri-stack-line text-primary-600" aria-hidden="true" /> 相关推荐
          </h2>
          <Link to={`/channel/${article.channel}`} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
            更多该栏目内容 <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
      </section>
    </div>
  );
}